import { API_BASE_URL, request } from './api';

type RawProviderEntry = {
  id: string;
  name: string;
  icon: string;
  category: 'meeting' | 'service' | 'restaurant' | 'tour';
  supportsEmbed: boolean;
  embedStrategy: 'iframe' | 'script+container' | 'link';
  frameSrcOrigins: string[];
  frameabilityTTL: number;
  notes: string | null;
  urlPatterns: Array<{
    pattern: string;
    flags: string;
  }>;
};

export type ProviderRegistryEntry = RawProviderEntry & {
  compiledPatterns: RegExp[];
};

type ProviderRegistryResponse = {
  version: string;
  providers: RawProviderEntry[];
};

export async function fetchProviderRegistry(): Promise<{
  version: string;
  providers: ProviderRegistryEntry[];
}> {
  const payload = await request<ProviderRegistryResponse>('/tap/providers', {
    method: 'GET'
  });

  return {
    version: payload.version,
    providers: payload.providers.map((entry) => ({
      ...entry,
      compiledPatterns: entry.urlPatterns.map((pattern) => new RegExp(pattern.pattern, pattern.flags || undefined))
    }))
  };
}

export function matchesProviderUrl(entry: ProviderRegistryEntry, url: string | undefined): boolean {
  if (!url) {
    return false;
  }
  const trimmed = url.trim();
  if (!trimmed) {
    return false;
  }
  return entry.compiledPatterns.some((pattern) => pattern.test(trimmed));
}

export type FrameabilityStatus = {
  providerId: string;
  normalizedUrl: string;
  status: 'OK' | 'BLOCKED' | 'UNKNOWN';
  reason: string | null;
  checkedAt: string | null;
  expiresAt: string | null;
  stale: boolean;
};

export async function fetchFrameabilityStatus(
  providerId: string,
  url: string,
  signal?: AbortSignal
): Promise<FrameabilityStatus> {
  const params = new URLSearchParams({
    providerId,
    url
  });

  const response = await fetch(`${API_BASE_URL}/tap/providers/frameability?${params.toString()}`, {
    signal,
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error('frameability_fetch_failed');
  }

  return (await response.json()) as FrameabilityStatus;
}
